import * as Sentry from "@sentry/react";
import { Box, Button } from "@mui/material";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import { getChatDeletedMessage, isChatDeletedError } from "hhn-commons";
import { enqueueSnackbar } from "notistack";
import React from "react";
import CenterLayout from "./layout/CenterLayout";
import { ROUTES } from "./routes";
import { extractOrganizationId, getHomeRoute } from "./utils/sessionRoute";
import { collectCrashContext } from "./utils/crashContext";
import { IDomain, Unsubscribe } from "@home-health-notify/domain";

type ErrorBoundaryProps = {
  children: React.ReactNode;
  domain: IDomain;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

type CrashDetail = {
  name: string;
  message: string;
  stack?: string;
};

// The browser fires these to say it deferred work to a later frame, not that
// anything failed; the spec expects them to be ignored. Extensions that overlay
// the message composer (QuillBot's qb-div, Grammarly's grammarly-mirror) size
// themselves against the textarea and provoke the ResizeObserver one
// constantly, so treating it as a crash put the fallback screen in front of
// users mid-message. Report it, but leave the app standing.
const NON_FATAL_WINDOW_ERRORS = [/^ResizeObserver loop/];

function isNonFatalWindowError(message: string): boolean {
  return NON_FATAL_WINDOW_ERRORS.some(pattern => pattern.test(message));
}

function describeCrash(error: unknown): CrashDetail {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message || error.name,
      stack: error.stack,
    };
  }
  if (typeof error === "string") {
    return { name: "Error", message: error };
  }
  return { name: "Error", message: String(error) };
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private domainRef: { current: IDomain };
  private removeDomainChangeListener?: Unsubscribe;
  // A looping ResizeObserver fires hundreds of times a second. The app now
  // survives it, so without this every loop would bill a Sentry event.
  private readonly reportedNonFatal = new Set<string>();

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this.domainRef = { current: props.domain };
    this.promiseRejectionHandler = this.promiseRejectionHandler.bind(this);
    this.errorEventHandler = this.errorEventHandler.bind(this);
  }

  private setupDomainListener(domain: IDomain) {
    this.removeDomainChangeListener?.();
    this.domainRef.current = domain;
    this.removeDomainChangeListener = domain.registerChangeListener(
      (newDomain: IDomain) => {
        this.setupDomainListener(newDomain);
      },
    );
  }

  private destroyOrgUsers() {
    this.domainRef.current.user?.destroyOrgUsers().catch(() => {});
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  promiseRejectionHandler(event: PromiseRejectionEvent): void {
    const isInvalidAuthTokenError =
      event.reason instanceof Error &&
      typeof (event.reason as any).code === "string" &&
      (event.reason as any).code === "FORCE_LOGOUT";
    const isSyncError =
      event.reason instanceof Error &&
      typeof (event.reason as any).code === "string" &&
      (event.reason as any).code === "SYNC_ERROR";
    const isNoInternetError =
      event.reason instanceof Error &&
      typeof (event.reason as any).code === "string" &&
      (event.reason as any).code === "NO_INTERNET";
    const isNegativeUnreadCountError =
      event.reason instanceof Error &&
      typeof (event.reason as any).code === "string" &&
      (event.reason as any).code === "NEGATIVE_UNREAD_COUNT_ERROR";
    const isOfflineTimoutError =
      event.reason instanceof Error &&
      typeof (event.reason as any).code === "string" &&
      (event.reason as any).code === "OFFLINE_TIMEOUT";

    if (isInvalidAuthTokenError || isNoInternetError) {
      event.preventDefault();
    } else if (isOfflineTimoutError) {
      event.preventDefault();
      window.location.href = getHomeRoute();
    } else if (isSyncError) {
      Sentry.captureException(event.reason, {
        extra:
          (event.reason as any).info || (event.reason as any).syncErrorInfo,
      });
      const orgId = extractOrganizationId(window.location.pathname);
      if (orgId) {
        window.location.pathname = `${ROUTES.ORGANIZATIONS}/${orgId}`;
        event.preventDefault();
      }
    } else if (isNegativeUnreadCountError) {
      // Temporarily suppress this error to avoid disrupting user experience
      Sentry.captureException(event.reason);
      event.preventDefault();
    } else if (isChatDeletedError(event.reason)) {
      // Someone deleted the chat while it was open. The paths that await this
      // fetch inside a try/catch already say so and leave the app standing, so
      // the ones that do not must not get the fallback screen instead. The
      // response itself is still recorded by httpClientIntegration.
      event.preventDefault();
      enqueueSnackbar(getChatDeletedMessage(), {
        variant: "error",
        autoHideDuration: 3000,
      });
    } else {
      this.reportCrash(event.reason, "unhandledrejection");
      this.destroyOrgUsers();
      this.setState({ hasError: true });
    }
  }

  errorEventHandler(event: ErrorEvent): void {
    const isNegativeUnreadCountError =
      event.error && event.error.code === "NEGATIVE_UNREAD_COUNT_ERROR";
    // Temporarily suppress this error to avoid disrupting user experience
    if (isNegativeUnreadCountError) {
      return;
    }

    const error = event.error ?? new Error(event.message);

    if (isNonFatalWindowError(event.message ?? "")) {
      const key = event.message ?? "";
      if (!this.reportedNonFatal.has(key)) {
        this.reportedNonFatal.add(key);
        this.reportCrash(error, "window-onerror", { fatal: false });
      }
      return;
    }

    this.reportCrash(error, "window-onerror");
    this.destroyOrgUsers();
    this.setState({ hasError: true });
  }

  // Uncaught errors from event handlers / async code bypass React error
  // boundaries, so componentDidCatch never sees them and they were previously
  // never reported. Capture them here with PHI-safe extension/DOM context so we
  // can diagnose extension-triggered crashes (issue #1376) from production.
  //
  // Reporting the original error verbatim loses most of these before they leave
  // the browser: the SDK's default `ignoreErrors` list drops "Script error."
  // (what a cross-origin extension script produces) and ResizeObserver loop
  // warnings, and Dedupe drops whatever React already logged through console.
  // Anything reaching this method has put the fallback screen in front of a
  // user, so it is never noise — re-report it under a message and fingerprint
  // we own, keeping the original stack so grouping still follows the real code.
  private reportCrash(
    error: unknown,
    source: string,
    options?: { extra?: Record<string, unknown>; fatal?: boolean },
  ): string {
    const { extra, fatal = true } = options ?? {};
    const context = collectCrashContext();
    const { name, message, stack } = describeCrash(error);
    const label = fatal ? "App crash" : "Ignored window error";
    const crash = new Error(`${label} (${source}): ${name}: ${message}`);
    if (stack) {
      crash.stack = stack;
    }
    return Sentry.captureException(crash, {
      fingerprint: fatal
        ? ["error-boundary", source, name, message]
        : ["error-boundary", source, name, message, "non-fatal"],
      tags: {
        errorBoundarySource: source,
        hasExtensionDom: context.hasExtensionDom,
        fatal,
      },
      extra: {
        ...extra,
        crashContext: context,
        originalError: { name, message },
      },
    });
  }

  componentDidMount() {
    window.addEventListener("unhandledrejection", this.promiseRejectionHandler);
    window.addEventListener("error", this.errorEventHandler);
    this.setupDomainListener(this.props.domain);
  }

  componentWillUnmount() {
    window.removeEventListener(
      "unhandledrejection",
      this.promiseRejectionHandler,
    );
    window.removeEventListener("error", this.errorEventHandler);
    this.removeDomainChangeListener?.();
  }

  componentDidCatch(error: any, errorInfo: any): void {
    this.destroyOrgUsers();
    console.log("Error Boundary :: ", error);
    const eventId = this.reportCrash(error, "componentDidCatch", {
      extra: { ...errorInfo },
    });
    Sentry.showReportDialog({
      eventId,
      title: "Something went wrong",
      subtitle: "Please help us fix it by sharing what happened.",
    });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <CenterLayout>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ErrorOutlineOutlined fontSize="large" color="primary" />
            <h1>Something went wrong.</h1>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                window.location.href = getHomeRoute();
              }}
            >
              Refresh
            </Button>
          </Box>
        </CenterLayout>
      );
    }

    return this.props.children;
  }
}
